import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, ChevronRight, Keyboard, PackageOpen, Search } from 'lucide-react';
import { ScanOverlay } from '@/components/dashboard/ScanOverlay';
import { apiFetch, ApiError } from '@/lib/api';
import { scanDeuten } from '@/lib/scan';
import { isbnAnzeige } from '@/lib/isbn';
import { datumKurzDe } from '@/lib/format';
import { useToast } from '@/lib/toast';
import { useZeigerFein } from '@/hooks/useLayout';
import type { ScanSucheAntwort, ScanTreffer, ScanWeg } from './typen';
import { FOKUS_RING_HELL } from '@/lib/fokus';
import { melde } from '@/lib/nutzung';

// Ein Scanner, zwei Modi.
//
// WARUM DIESE DATEI ÜBERHAUPT
//
// Der Modus muss oberhalb von ScanOverlay liegen, aber unterhalb des
// Umschaltens. Läge er beim Aufrufer, würde ein Wechsel das Overlay
// aus- und wieder einhängen — und damit die Kamera neu starten. Genau
// das soll nicht passieren, wenn jemand merkt, dass er im falschen Modus
// gelandet ist. ScanOverlay bleibt deshalb ununterbrochen im Baum; hier
// wechselt nur, was der gelesene Code bedeutet.
//
// SUCHEN
//   Wie bisher: ein Treffer, Fenster zu, der Aufrufer entscheidet.
//
// WARENEINGANG
//   Dauerbetrieb. Gelesen wird die ISBN vom Buchrücken, gefragt wird
//   "welche offene Kundenbestellung will dieses Buch", gebucht wird mit
//   einem Tipp — dann ist der Scanner sofort wieder scharf. Zwischen
//   Buch und Buch bleibt die Kamera an.
//
// DAS KURZJOURNAL (A22)
//   Bis dahin sagte der Scanner nur, WIE VIELE Buchungen stattgefunden
//   hatten — nicht, wohin sie gingen. Wer zehn Bücher hintereinander
//   scannt, weiß danach, dass zehnmal gebucht wurde, aber nicht, ob das
//   siebte beim richtigen Kunden gelandet ist, und kommt nicht hin, ohne
//   das Fenster zu schließen und zu suchen.
//
//   Deshalb steht jetzt über dem Bild, was gerade geschehen ist: Titel,
//   Kunde, Bestellnummer, neueste oben — und jede Zeile führt in ihre
//   Bestellung.
//
//   ES IST EINE BESCHREIBUNG, KEIN SPEICHER. Beim Schließen des Fensters
//   verschwindet es. Dauerhaft steht jede Buchung ohnehin im Verlauf
//   ihrer Bestellung; ein zweiter Ort dafür wäre eine zweite Wahrheit
//   (Designprinzip 4).

export type ScanModus = 'suche' | 'wareneingang';

type Props = {
  startModus: ScanModus;
  onSchliessen: () => void;
  /**
   * Ein Code im Suchmodus. Der rohe Text — gedeutet wird beim Aufrufer,
   * so wie vorher auch.
   */
  onSuchTreffer: (roh: string) => void;
  /** Nach einer Buchung: Listen neu holen. */
  onGebucht?: () => void;
};

/**
 * Wie viele Buchungen das Kurzjournal zeigt.
 *
 * SECHS, und die Zahl ist gemessen, nicht geschätzt. Auf 390 × 844 —
 * dem engsten Gerät im Laden — bleiben über dem Zielrahmen der Kamera
 * rund 270 Pixel frei, bevor er zugedeckt würde. Eine Zeile ist ein
 * Berührziel, also mindestens 44 Pixel hoch: 270 / 44 ≈ 6.
 *
 * Fünf wären knapp, wenn jemand ein Paket mit zwanzig Büchern auspackt;
 * zwanzig deckten die Kamera zu, und die Kamera ist das Werkzeug. Sechs
 * decken einen gewöhnlichen Paketlauf am Stück ab.
 *
 * Nichts geht dabei verloren: Der Zähler in der Fußleiste führt die
 * VOLLSTÄNDIGE Zahl weiter, auch wenn die Liste nur die letzten sechs
 * zeigt. Deshalb ist er auch nicht im Journal aufgegangen.
 */
const JOURNAL_MAX = 6;

/**
 * Eine Zeile des Kurzjournals.
 *
 * Sie entsteht aus dem TREFFER, der eben gebucht wurde — nicht aus einem
 * zweiten Abruf. Titel, Kunde und Bestellnummer standen schon in der
 * Antwort der Suche, die dieser Buchung vorausging; sie liegen zum
 * Zeitpunkt des Buchens ohnehin in der Hand. Zehn Scans kosten damit
 * zehn Anfragen, nicht zwanzig.
 *
 * `nr` ist eine laufende Nummer und kein Schlüssel aus der Datenbank:
 * dieselbe Position kann zweimal gebucht werden (zwei Exemplare), und
 * beide Buchungen sind eigene Zeilen.
 */
type JournalEintrag = {
  nr: number;
  bestellnr: string;
  titel: string;
  kunde: string;
};

type Lage =
  | { art: 'bereit' }
  | { art: 'sucht'; isbn: string }
  // `text`: gesucht wurde nach Titel/Autor, nicht nach einem Code.
  | { art: 'leer'; isbn: string; text: boolean }
  | { art: 'kein-buch'; roh: string }
  | { art: 'treffer'; isbn: string; treffer: ScanTreffer[] };

export function ScanFenster({ startModus, onSchliessen, onSuchTreffer, onGebucht }: Props) {
  const toast = useToast();
  const [modus, setModus] = useState<ScanModus>(startModus);
  const [lage, setLage] = useState<Lage>({ art: 'bereit' });
  const [gebucht, setGebucht] = useState(0);
  const [journal, setJournal] = useState<JournalEintrag[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  // Zählt hoch, wann immer der Scanner wieder auf Empfang geht. Das
  // Tastaturfeld holt sich daran den Fokus zurück (siehe TastaturFeld).
  const [empfang, setEmpfang] = useState(0);
  const navigate = useNavigate();

  // Der Modus im Rückruf muss der aktuelle sein, nicht der von damals.
  // ScanOverlay hält den Rückruf in einer Referenz fest, damit ein neuer
  // nicht die Kamera neu startet — ohne diese Referenz hier läse ein
  // frisch umgeschalteter Scanner noch nach der alten Regel.
  const modusRef = useRef(modus);
  modusRef.current = modus;

  // Nur am Geraet mit Maus bekommt das Tastaturfeld von selbst den Fokus
  // (A35). Am Tablet zoege das die Bildschirmtastatur auf — ueber die
  // Kamera, also ueber genau das, worauf es dort ankommt.
  const zeigerFein = useZeigerFein();

  const zurueck = useCallback((): void => {
    setLage({ art: 'bereit' });
    setEmpfang((n) => n + 1);
  }, []);

  const onCode = useCallback(
    (roh: string): void => {
      if (modusRef.current === 'suche') {
        onSchliessen();
        onSuchTreffer(roh);
        return;
      }

      // Wareneingang: nur eine ISBN vom Buchrücken ergibt hier Sinn. Ein
      // Zettel-QR ist kein Wareneingang — wer den scannt, wollte in die
      // Bestellung, und das sagt die Meldung auch.
      //
      // A28 (#39): Hier steht bewusst KEINE Host-Liste. An dieser Stelle
      // kann sie das Ergebnis nicht ändern — alles außer „suche“ landet
      // in derselben Meldung, und ob ein Zettel-QR als „nummer“ oder als
      // „fremd“ gedeutet wird, ist genau dieser Unterschied. Die Liste
      // hier anzuhängen hieße: eine Abfrage der Einstellungen in den
      // Wareneingang zu legen, die nichts entscheidet. (Nachgemessen an
      // den Codes, die es wirklich gibt: Zettel-QR `/s/{Bestellnr}` und
      // Buch-Barcode.)
      const deutung = scanDeuten(roh);
      if (deutung.art !== 'suche') {
        setLage({ art: 'kein-buch', roh });
        return;
      }
      const isbn = deutung.wert;
      setLage({ art: 'sucht', isbn });
      void (async () => {
        try {
          const res = await apiFetch<ScanSucheAntwort>(
            // `q`: Code ODER Titel/Autor — der Server entscheidet
            // (Befund 29.10.2026: „Ahriman kommt" fand nichts).
            `/wareneingang/suche.php?q=${encodeURIComponent(isbn)}`
          );
          if (res.treffer.length === 0) {
            // Nutzungsmessung: nur die TEXTsuche. Ein gescannter Code ohne
            // Treffer heißt „niemand wartet darauf" — das ist kein Fehler.
            if (res.isbn === null) melde('suche_ohne_treffer', { ort: 'wareneingang', text: isbn });
            setLage({ art: 'leer', isbn: res.isbn ?? isbn, text: res.isbn === null });
            return;
          }
          setLage({ art: 'treffer', isbn: res.isbn ?? isbn, treffer: res.treffer });
        } catch (e) {
          const text = e instanceof ApiError ? e.message : 'Die Suche schlug fehl.';
          toast('error', text);
          setLage({ art: 'bereit' });
        }
      })();
    },
    [onSchliessen, onSuchTreffer, toast]
  );

  async function buchen(t: ScanTreffer, weg: ScanWeg | null): Promise<void> {
    if (laeuft) return;
    setLaeuft(true);
    try {
      await apiFetch('/bestellungen/positionen/eingang.php', {
        method: 'POST',
        body: JSON.stringify({
          position_id: t.position_id,
          // Der Endpunkt bekommt den ZIELWERT, nicht die Differenz — wie
          // überall sonst auch. Ein Stück mehr als bisher.
          menge_eingegangen: t.menge_eingegangen + 1,
          ...(weg === null ? {} : { beschaffung_id: weg.beschaffung_id })
        })
      });
      setGebucht((g) => g + 1);
      // Neueste oben, und die Liste bleibt gedeckelt. `slice` NACH dem
      // Voranstellen: sonst fiele die frische Zeile heraus, sobald das
      // Journal voll ist.
      setJournal((j) =>
        [
          {
            nr: j.length === 0 ? 1 : j[0].nr + 1,
            bestellnr: t.bestellung.bestellnr,
            titel: t.titel,
            kunde: t.kunde.anzeigename
          },
          ...j
        ].slice(0, JOURNAL_MAX)
      );
      onGebucht?.();
      // KEIN Erfolgs-Toast (A25). Das Journal oben sagt dasselbe und mehr:
      // Titel und Kunde statt nur der Nummer, es bleibt stehen statt nach
      // fünf Sekunden zu verschwinden, und es führt mit einem Tipp in die
      // Bestellung. Der Toast lag zudem GENAU ÜBER der frischen
      // Journal-Zeile — auf 390 px verdeckte er sie fast ganz. Zwei
      // Meldungen über dieselbe Sache sind zwei Wahrheiten
      // (Designprinzip 4), und hier war die schlechtere von beiden auch
      // noch die obenauf.
      //
      // Der FEHLER-Toast unten bleibt. Er ist der einzige Kanal, über den
      // eine misslungene Buchung sichtbar wird: Das Journal bekommt in
      // diesem Fall keine Zeile, der Zähler springt nicht — ohne den Toast
      // bucht jemand ins Leere und merkt es nicht.
      //
      // Zurück auf Empfang. Das nächste Buch liegt schon in der Hand.
      setLage({ art: 'bereit' });
      setEmpfang((n) => n + 1);
    } catch (e) {
      const text = e instanceof ApiError ? e.message : 'Das Eintragen schlug fehl.';
      toast('error', text);
    } finally {
      setLaeuft(false);
    }
  }

  /**
   * Der Weg in die Bestellung (A22.2).
   *
   * Das Fenster schließt sich dabei, und das ist kein Nebeneffekt: Man
   * verlässt den Wareneingang. Die Bestellung läge sonst hinter einem
   * bildschirmfüllenden Overlay, und die Kamera liefe weiter — eine
   * Kamera, die im Hintergrund anbleibt, ist im Laden ein
   * Vertrauensproblem (siehe ScanOverlay).
   *
   * Von selbst passiert das NIE. Der Weg ist ein Angebot: keine Buchung
   * springt dorthin, nichts hält den nächsten Scan auf. Wer weiterpackt,
   * merkt vom Journal nichts außer der Zeile, die dazukommt.
   */
  function zurBestellung(e: JournalEintrag): void {
    onSchliessen();
    void navigate({ to: '/bestellungen/$bestellnr', params: { bestellnr: e.bestellnr } });
  }

  const angehalten = lage.art !== 'bereit';

  // Escape am PC: eine offene Trefferliste zuerst zuklappen, erst dann
  // das Fenster. Sonst nimmt ein Tastendruck Liste UND Kurzjournal mit.
  const beiEscape = useCallback((): void => {
    if (lage.art !== 'bereit') zurueck();
    else onSchliessen();
  }, [lage.art, zurueck, onSchliessen]);

  return (
    <ScanOverlay
      onSchliessen={onSchliessen}
      onCode={onCode}
      angehalten={angehalten}
      // Das Tastaturfeld ist ein vollwertiger zweiter Eingang. Eine
      // fehlende Kamera ist damit kein Ende, sondern am Büro-PC der
      // Normalfall — siehe ScanOverlay.
      tastaturWeg
      onEscape={beiEscape}
      // „Schließen", nicht „Abbrechen": Gebuchtes bleibt gebucht. Das Wort
      // „Abbrechen" ließ vermuten, das Schließen mache die Buchungen
      // rückgängig.
      schliessenText="Schließen"
      hinweis={
        <div className="min-w-0 flex-1 space-y-2">
          <TastaturFeld onCode={onCode} autofokus={zeigerFein} empfang={empfang} />
          <FussLeiste
            modus={modus}
            gebucht={gebucht}
            onWechsel={() => setModus((m) => (m === 'suche' ? 'wareneingang' : 'suche'))}
          />
        </div>
      }
      oben={
        // Nur im Wareneingang: gebucht wird nur dort, und im Suchmodus
        // schließt das Fenster ohnehin beim ersten Treffer.
        modus === 'wareneingang' && journal.length > 0 ? (
          <Kurzjournal eintraege={journal} onOeffnen={zurBestellung} />
        ) : undefined
      }
      einblendung={
        modus === 'wareneingang' && lage.art !== 'bereit' ? (
          <Einblendung
            lage={lage}
            laeuft={laeuft}
            onWeiter={zurueck}
            onBuchen={(t, w) => void buchen(t, w)}
          />
        ) : undefined
      }
    />
  );
}

// ---------------------------------------------------------------------

/**
 * Was gerade eingetragen wurde — und der Weg dorthin.
 *
 * Gedämpft gehalten: Der Blick gehört der Kamera, nicht dieser Liste.
 * Sie beantwortet eine Frage, die man erst stellt, wenn man stutzig
 * wird ("war das siebte richtig?"), und drängt sich bis dahin nicht auf.
 */
function Kurzjournal({
  eintraege,
  onOeffnen
}: {
  eintraege: JournalEintrag[];
  onOeffnen: (e: JournalEintrag) => void;
}) {
  return (
    <div className="space-y-1">
      <p className="px-1 text-xs text-cream/60">Gerade eingetragen</p>
      <ul className="space-y-1">
        {eintraege.map((e) => (
          <li key={e.nr}>
            {/* Ein Knopf, kein Link mit Hover-Wirkung: auf dem Tablet
                gibt es kein Überfahren, und 44 Pixel sind hier keine
                Empfehlung, sondern die Größe eines Fingers. */}
            <button
              type="button"
              onClick={() => onOeffnen(e)}
              className="flex min-h-[44px] w-full items-center gap-2 rounded-md border border-cream/25 bg-ink/80 px-3 py-1.5 text-left hover:border-cream/60 hover:bg-ink"
              aria-label={`${e.titel} — ${e.kunde}, ${e.bestellnr} öffnen`}
            >
              <Check aria-hidden className="h-4 w-4 shrink-0 text-cream/70" />
              <span className="min-w-0 flex-1">
                {/* `truncate`: ein langer Titel darf die Zeile nicht
                    aufziehen, sonst wächst die Leiste über den
                    Zielrahmen. */}
                <span className="block truncate text-sm text-cream">{e.titel}</span>
                <span className="block truncate text-xs text-cream/60">
                  {e.kunde} · {e.bestellnr}
                </span>
              </span>
              <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-cream/50" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Der Handscanner am Buero-PC (A35, #97).
 *
 * EIN USB-HANDSCANNER IST EINE TASTATUR. Er oeffnet keinen Browser und
 * ruft keine Adresse auf — er TIPPT den Inhalt des Codes dorthin, wo der
 * Fokus steht, und haengt ein Enter an. Ohne ein Feld, das ihn
 * entgegennimmt, landet ein Scan am Rechner im Nichts: Der Zettel-QR
 * traegt eine Adresse, und die Kamera-Wege lesen sie, die Tastatur
 * bisher nirgends.
 *
 * DERSELBE WEG WIE DIE KAMERA. Das Feld ruft `onCode` — genau den
 * Rueckruf, den auch der Decoder ruft. Keine zweite Auswertung: zwei
 * Deutungen desselben Codes laufen frueher oder spaeter auseinander
 * (Fallstrick 15), und dann liest die Kamera einen Zettel, den die
 * Tastatur verwirft.
 *
 * WARUM IMMER SICHTBAR, NICHT NUR OHNE KAMERA
 *   Ein Buero-PC hat oft eine Webcam. Sie taugt selten zum Scannen, aber
 *   sie meldet sich — dann liefe die Kamera, und ein Feld, das nur bei
 *   „keine Kamera" erschiene, waere gerade dort weg, wo der Handscanner
 *   steckt. Sichtbar kostet es am Tablet eine Zeile in der Fussleiste
 *   und keinen einzigen Schritt: Die Kamera braucht keinen Fokus.
 *
 * DER FOKUS IST DIE HALBE MIETE, und deshalb die Bedingung `autofokus`:
 * Am PC (Maus) gehoert er dem Feld, sonst tippt der Scanner ins Leere
 * und der Mensch sucht den Fehler im Geraet. Am Tablet bekaeme mit dem
 * Fokus die Bildschirmtastatur die halbe Hoehe — ueber der Kamera.
 *
 * NACH DEM AUSLOESEN LEER UND WEITER SCHARF. Das ist der Fall, den man
 * mit einem Buch nie sieht und mit zwanzig sofort: Bliebe der erste Code
 * stehen, haenge der zweite hinten dran und ergaebe Unsinn.
 */
function TastaturFeld({
  onCode,
  autofokus,
  empfang
}: {
  onCode: (roh: string) => void;
  autofokus: boolean;
  /**
   * Zählt hoch, sobald der Scanner wieder auf Empfang ist — nach einer
   * Buchung, nach „Überspringen", nach „Weiter scannen".
   *
   * Der Klick auf „Eingetroffen" nimmt dem Feld den Fokus: er landet auf
   * dem Knopf. Der nächste Handscanner-Schuss tippte danach ins Leere,
   * und man musste vor jedem Buch erst wieder ins Feld klicken. Am PC
   * holt sich das Feld den Fokus deshalb bei jedem neuen Empfang zurück.
   */
  empfang: number;
}) {
  const [wert, setWert] = useState('');
  const feldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autofokus) feldRef.current?.focus();
  }, [autofokus, empfang]);

  function ausloesen(): void {
    const roh = wert.trim();
    if (roh === '') return;
    // Erst leeren und den Fokus halten, dann deuten: `onCode` kann das
    // Fenster schliessen (Suchmodus), und danach gibt es hier nichts
    // mehr zu leeren.
    setWert('');
    feldRef.current?.focus();
    onCode(roh);
  }

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        // Das Enter des Handscanners laeuft hier durch. Ein `form` und
        // nicht ein Tasten-Horcher: So loest auch die Eingabetaste einer
        // gewoehnlichen Tastatur aus, ohne dass jemand das nachbauen
        // muss.
        e.preventDefault();
        ausloesen();
      }}
    >
      <label className="sr-only" htmlFor="scan-tastatur">
        Code mit dem Handscanner oder von Hand eingeben
      </label>
      <Keyboard aria-hidden className="h-4 w-4 shrink-0 text-cream/60" />
      <input
        id="scan-tastatur"
        ref={feldRef}
        type="text"
        value={wert}
        onChange={(e) => setWert(e.target.value)}
        autoComplete="off"
        placeholder="Handscanner oder Eingabe"
        className={`min-h-[44px] min-w-0 flex-1 rounded-md border border-cream/40 bg-ink px-3 py-2 text-sm text-cream placeholder:text-cream/50 ${FOKUS_RING_HELL}`}
      />
      {/* Der Knopf schadet nicht und traegt den Fall, in dem jemand von
          Hand tippt und die Maus schon in der Hand hat. */}
      <button
        type="submit"
        disabled={wert.trim() === ''}
        className="inline-flex min-h-[44px] shrink-0 items-center rounded-md border border-cream/40 px-3 text-sm text-cream hover:bg-cream/10 disabled:opacity-50"
      >
        Lesen
      </button>
    </form>
  );
}

function FussLeiste({
  modus,
  gebucht,
  onWechsel
}: {
  modus: ScanModus;
  gebucht: number;
  onWechsel: () => void;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
      <div className="min-w-0">
        {/* Kurz genug für 390 Pixel: daneben stehen noch der Umschalter
            und „Abbrechen". Beim ersten Anlauf war der Text so lang, dass
            er nach vier Wörtern abgeschnitten wurde. */}
        <p className="truncate text-sm text-cream/90">
          {modus === 'suche' ? 'Zettel-QR oder Barcode' : 'Ware eintragen'}
        </p>
        {modus === 'wareneingang' && gebucht > 0 && (
          <p className="text-xs text-cream/60">
            {gebucht === 1 ? '1 Stück eingetragen' : `${gebucht} Stück eingetragen`}
          </p>
        )}
      </div>
      {/* Der Umschalter sitzt hier und nicht am Einstieg: die Kamera
          bleibt beim Wechsel an. Wer im falschen Modus gelandet ist,
          verliert keine Sekunde. */}
      <button
        type="button"
        onClick={onWechsel}
        className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-md border border-cream/40 px-3 text-sm text-cream hover:bg-cream/10"
      >
        {modus === 'suche' ? (
          <>
            <PackageOpen aria-hidden className="h-4 w-4" />
            Wareneingang
          </>
        ) : (
          <>
            <Search aria-hidden className="h-4 w-4" />
            Suchen
          </>
        )}
      </button>
    </div>
  );
}

function Einblendung({
  lage,
  laeuft,
  onWeiter,
  onBuchen
}: {
  lage: Lage;
  laeuft: boolean;
  onWeiter: () => void;
  onBuchen: (t: ScanTreffer, weg: ScanWeg | null) => void;
}) {
  if (lage.art === 'bereit') return null;

  if (lage.art === 'sucht') {
    return (
      <Kasten>
        <p className="text-sm text-ink-soft">Suche offene Bestellungen …</p>
      </Kasten>
    );
  }

  if (lage.art === 'kein-buch') {
    return (
      <Kasten>
        <p className="font-serif text-base text-ink">Das ist kein Buch-Barcode</p>
        <p className="mt-1 text-sm text-ink-soft">
          Für den Wareneingang brauchst du den Strichcode vom Buchrücken. Willst du
          eine Bestellung öffnen, schalt unten auf „Suchen“ um.
        </p>
        <Weiter onWeiter={onWeiter} />
      </Kasten>
    );
  }

  if (lage.art === 'leer') {
    return (
      <Kasten>
        <p className="font-serif text-base text-ink">Keine offene Kundenbestellung</p>
        {lage.text ? (
          <p className="mt-1 text-sm text-ink-soft">
            Zu „{lage.isbn}“ passt kein offener Titel. Prüf die Schreibweise oder
            scanne den Strichcode vom Buchrücken.
          </p>
        ) : (
          <p className="mt-1 text-sm text-ink-soft">
            Zu {isbnAnzeige(lage.isbn)} wartet niemand. Das Buch gehört in den Bestand
            oder zu einer Bestellung, die schon vollständig eingetroffen ist.
          </p>
        )}
        <Weiter onWeiter={onWeiter} />
      </Kasten>
    );
  }

  return (
    <Kasten>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-serif text-base text-ink">
          {lage.treffer.length === 1
            ? 'Eine offene Bestellung'
            : `${lage.treffer.length} offene Bestellungen`}
        </p>
        <span className="shrink-0 text-xs text-ink-faint">{isbnAnzeige(lage.isbn)}</span>
      </div>
      {/* Älteste zuerst — so kommt der Server sie schon. Wer lange
          wartet, bekommt das erste Exemplar. */}
      <ul className="mt-2 divide-y divide-line">
        {lage.treffer.map((t) => (
          <TrefferZeile key={t.position_id} t={t} laeuft={laeuft} onBuchen={onBuchen} />
        ))}
      </ul>
      <Weiter onWeiter={onWeiter} text="Überspringen" />
    </Kasten>
  );
}

function TrefferZeile({
  t,
  laeuft,
  onBuchen
}: {
  t: ScanTreffer;
  laeuft: boolean;
  onBuchen: (t: ScanTreffer, weg: ScanWeg | null) => void;
}) {
  const offeneWege = t.beschaffungen.filter((w) => w.offen > 0);
  // Genau ein offener Weg heißt: ohne Rückfrage buchen (E2). Keiner
  // heißt: unzugeordnet, auch das ohne Rückfrage — eine Kiste ohne
  // erkennbaren Absender ist ein gültiger Vorgang.
  const ohneRueckfrage = offeneWege.length <= 1;

  return (
    <li className="py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate font-serif text-sm text-ink">
          {t.kunde.anzeigename}
        </span>
        <span className="shrink-0 font-serif text-xs text-ink-faint">
          {t.bestellung.bestellnr}
        </span>
      </div>
      <p className="mt-0.5 truncate text-xs text-ink-soft">{t.titel}</p>
      <p className="mt-0.5 text-xs text-ink-faint">
        {t.offen} von {t.menge} offen
        {t.bestellung.ist_uebung && ' · Übung'}
        {t.bestellung.zustand.ist_endzustand && ` · ${t.bestellung.zustand.label}`}
      </p>
      {/* Lieferantenmeldungen (E3) stehen dezent dabei — sie erklären oft
          genau, warum hier noch etwas offen ist. Ausgelegt wird nichts. */}
      {offeneWege
        .filter((w) => w.meldung !== null && w.meldung !== '')
        .map((w) => (
          <p
            key={w.beschaffung_id}
            className="mt-0.5 flex gap-1 text-xs italic text-gold-dark"
          >
            <span className="shrink-0 not-italic text-ink-faint">
              {w.label}
              {w.meldung_am !== null && `, ${datumKurzDe(w.meldung_am)}`}:
            </span>
            <span className="min-w-0 truncate">{w.meldung}</span>
          </p>
        ))}

      {ohneRueckfrage ? (
        <button
          type="button"
          disabled={laeuft}
          onClick={() => onBuchen(t, offeneWege[0] ?? null)}
          className="mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-akzent px-4 text-sm font-medium text-cream hover:bg-akzent-stark disabled:opacity-50"
        >
          <Check aria-hidden className="h-4 w-4" />
          Eingetroffen
          {offeneWege[0] !== undefined && ` — ${offeneWege[0].label}`}
        </button>
      ) : (
        <>
          {/* Mehrere Wege offen: das entscheidet der Mensch, nicht der
              Server. Ein Tipp pro Weg — mehr Rückfrage gibt es nicht. */}
          <p className="mt-2 text-xs text-ink-faint">Von wem kam es?</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {offeneWege.map((w) => (
              <button
                key={w.beschaffung_id}
                type="button"
                disabled={laeuft}
                onClick={() => onBuchen(t, w)}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-md border border-akzent px-3 text-sm text-akzent-stark hover:bg-akzent-sanft disabled:opacity-50"
              >
                {w.label} ({w.offen})
              </button>
            ))}
          </div>
        </>
      )}
    </li>
  );
}

/**
 * Der Kasten über dem Bild.
 *
 * Die Breite ist begrenzt (max-w-lg am Aufrufer in ScanOverlay): am
 * Bürorechner liefen die Knöpfe sonst über 1440 Pixel auseinander, und
 * ein „Eingetroffen" über die ganze Bildschirmbreite ist kein Knopf
 * mehr, sondern ein Balken.
 */
function Kasten({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-t-lg border border-b-0 border-line bg-cream p-4">{children}</div>
  );
}

function Weiter({ onWeiter, text = 'Weiter scannen' }: { onWeiter: () => void; text?: string }) {
  return (
    <button
      type="button"
      onClick={onWeiter}
      className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-md border border-line px-4 text-sm text-ink-soft hover:bg-cream-dark hover:text-ink"
    >
      {text}
    </button>
  );
}
